import {useMemo} from 'react';
import {STALE_TIME} from '@/utils/reactQueryClient';
import {CampaignStatsCreator} from '@/types/campaignStatsCreator';
import campaignStatsCreatorsFetchers, {
  CampaignStatsCreatorsResponse,
} from '@/api/fetchers/campaignStatsCreators';
import {DEFAULT_SORT} from '@/views/campaigns/stats/components/creators-stats-table/constants';
import useInfiniteDataQuery from '@/hooks/use-infinite-data-query';

export const QUERY_KEY = 'campaignStatsCreators';

const DEFAULT_PAGE_SIZE = 10;

interface UseCampaignStatsCreatorsQueryParams {
  campaignId: string;
  sort?: string;
  q?: string;
  perPage?: number;
  enabled?: boolean;
}

interface UseCampaignStatsCreatorsQueryResult {
  creators: CampaignStatsCreator[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  error: Error | null;
  totalCount: number;
}

function useCampaignStatsCreatorsQuery({
  campaignId,
  sort = DEFAULT_SORT,
  q = '',
  perPage = DEFAULT_PAGE_SIZE,
  enabled = true,
}: UseCampaignStatsCreatorsQueryParams): UseCampaignStatsCreatorsQueryResult {
  const queryResult = useInfiniteDataQuery({
    queryKey: [QUERY_KEY, campaignId, sort, q, perPage],
    queryFn: ({pageParam = 1}: {pageParam?: number}) =>
      campaignStatsCreatorsFetchers.fetchCreators({
        campaignId,
        sort,
        q,
        perPage,
        page: pageParam,
      }),
    getNextPageParam: (lastPage: CampaignStatsCreatorsResponse) =>
      lastPage.pagination.nextPage ?? undefined,
    enabled: enabled && !!campaignId,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
  });

  const {pages = []} = queryResult as {
    pages?: CampaignStatsCreatorsResponse[];
  };

  const creators = useMemo(() => {
    if (!pages.length) {
      return [];
    }
    return pages.flatMap((page) => page.creators);
  }, [pages]);

  const totalCount = useMemo(() => {
    if (!pages[0]) {
      return 0;
    }
    return pages[0].pagination.totalEntries;
  }, [pages]);

  return {
    creators,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    hasNextPage: queryResult.hasNextPage ?? false,
    fetchNextPage: queryResult.fetchNextPage,
    refetch: queryResult.refetch,
    error: queryResult.error as Error | null,
    totalCount,
  };
}

export default useCampaignStatsCreatorsQuery;
